import api from './api';

export interface SalaryServiceVariant {
  id: string;
  serviceId: string;
  name: string;
  rateType: 'fixed' | 'percentage';
  fixedPrice?: number | null;
  percentage?: number | null;
  bitrixProductId?: number | null;
  isActive: boolean;
  monthRates?: SalaryServiceVariantMonthRate[];
  createdAt: string;
  updatedAt: string;
}

export interface SalaryServiceVariantMonthRate {
  id?: string;
  variantId?: string;
  year: number;
  month: number;
  rateType: 'fixed' | 'percentage';
  fixedPrice?: number | null;
  percentage?: number | null;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SalaryService {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  variants: SalaryServiceVariant[];
}

export interface SalaryServiceVariantPayload {
  id?: string;
  name: string;
  rateType: 'fixed' | 'percentage';
  fixedPrice?: number | null;
  percentage?: number | null;
  bitrixProductId?: number | null;
  isActive?: boolean;
  monthRates?: SalaryServiceVariantMonthRate[];
}

export interface SalaryServicePayload {
  name: string;
  description?: string;
  isActive?: boolean;
  variants?: SalaryServiceVariantPayload[];
}

export type EmployeeRole = 'METROLOGIST' | 'DISPATCHER';

export interface Employee {
  id: string;
  fullName: string;
  city?: string;
  role: EmployeeRole;
  bitrixUserId?: number;
  isActive: boolean;
  inactiveFrom?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeePayload {
  fullName: string;
  city?: string;
  role: EmployeeRole;
  bitrixUserId?: number;
  isActive?: boolean;
  inactiveFromMonth?: number;
  inactiveFromYear?: number;
}

export interface DevelopmentEntryEmployeeRef {
  id: string;
  fullName: string;
  city?: string;
}

export interface BonusExtraEntry {
  id: string;
  city: string;
  employeeId: string;
  amount: number;
  date: string;
  comment?: string | null;
  employee?: DevelopmentEntryEmployeeRef;
  createdAt: string;
  updatedAt: string;
}

export interface GsmEntry {
  id: string;
  city: string;
  employeeId: string;
  amount: number;
  date: string;
  comment?: string | null;
  employee?: DevelopmentEntryEmployeeRef;
  createdAt: string;
  updatedAt: string;
}

export interface RevenueHandoverEntry {
  id: string;
  city: string;
  employeeId?: string | null;
  amount: number;
  date: string;
  comment?: string | null;
  employee?: DevelopmentEntryEmployeeRef;
  createdAt: string;
  updatedAt: string;
}

export interface ExpenseEntry {
  id: string;
  city: string;
  employeeId?: string | null;
  paymentType?: 'cash' | 'bank';
  amount: number;
  date: string;
  comment?: string | null;
  employee?: DevelopmentEntryEmployeeRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface PlanEntry {
  id: string;
  city: string;
  year: number;
  month: number;
  amount: number;
  comment?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type HouseholdItemType = 'water_counter' | 'heat_counter' | 'seal' | 'invoice';

export const HOUSEHOLD_ITEM_LABELS: Record<HouseholdItemType, string> = {
  water_counter: 'Счетчики воды',
  heat_counter: 'Счетчики тепла',
  seal: 'Пломбы',
  invoice: 'Накладные',
};

export interface HouseholdStockReceipt {
  id: string;
  city: string;
  itemType: HouseholdItemType;
  quantity: number;
  date: string;
  comment?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface HouseholdStockSummary {
  city: string;
  period: { from: string; to: string };
  accountingStart: string;
  employeeId: string | null;
  employeesCount: number;
  receivedTotal: Record<HouseholdItemType, number>;
  consumedInPeriod: Record<HouseholdItemType, number>;
  consumedToEnd: Record<HouseholdItemType, number>;
  balance: Record<HouseholdItemType, number>;
  dealsInPeriod: number;
}

export type HouseholdMovementScope = 'warehouse' | 'employee';
export type HouseholdMovementDirection = 'in' | 'out';
export type HouseholdMovementOperation = 'opening' | 'receipt' | 'issue' | 'consume' | 'return' | 'adjustment';

export interface HouseholdStockMovement {
  id: string;
  city: string;
  itemType: HouseholdItemType;
  quantity: number;
  scope: HouseholdMovementScope;
  direction: HouseholdMovementDirection;
  operationType: HouseholdMovementOperation;
  employeeId?: string | null;
  dealId?: string | null;
  dealUrl?: string | null;
  comment?: string | null;
  date: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  employee?: { id: string; fullName: string; city?: string | null } | null;
}

export interface HouseholdStockBalances {
  city: string;
  period: { from: string; to: string } | null;
  warehouse: Record<HouseholdItemType, number>;
  employees: Array<{
    employeeId: string;
    fullName: string;
    city?: string | null;
    balance: Record<HouseholdItemType, number>;
    periodSpent: Record<HouseholdItemType, number>;
    balanceTotal: number;
    periodSpentTotal: number;
  }>;
  periodStats: {
    warehouseIn: Record<HouseholdItemType, number>;
    warehouseOut: Record<HouseholdItemType, number>;
    employeeIn: Record<HouseholdItemType, number>;
    employeeOut: Record<HouseholdItemType, number>;
    consume: Record<HouseholdItemType, number>;
    issue: Record<HouseholdItemType, number>;
    return: Record<HouseholdItemType, number>;
  };
  movementsCount: number;
}

export interface ServiceCostEntry {
  id: string;
  city: string;
  year: number;
  month: number;
  waterVerification: number;
  waterReplacement: number;
  waterSale: number;
  waterSealing: number;
  waterDiagnostics: number;
  heatVerification: number;
  heatMounting: number;
  heatSale: number;
  heatBatteryReplacement: number;
  gasVerification: number;
  bankReceiptTaxPercent: number;
  perRequestExpense: number;
  dispatcherExpense: number;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessTrip {
  id: string;
  employeeId: string;
  city: string;
  from: string;
  to: string;
  comment?: string | null;
  createdAt: string;
  updatedAt: string;
  employee?: DevelopmentEntryEmployeeRef | null;
}

export type MetrologistWaterSummaryBlock = {
  verification: { amount: number; quantity: number; accrual: number };
  diagnosticsWater: { amount: number; quantity: number; accrual: number };
  waterCounters: { amount: number; quantity: number; accrual: number };
  replacement: { amount: number; quantity: number; accrual: number };
  sealing: { amount: number; quantity: number; accrual: number };
  accrualTotal: number;
  revenueTotal: number;
};

export type MetrologistHeatSummaryBlock = {
  verification: { quantity: number; amount?: number; accrual?: number };
  mounting: { quantity: number; amount?: number; accrual?: number };
  sale: { quantity: number; amount?: number; accrual?: number };
  batteryReplacement: { quantity: number; amount?: number; accrual?: number };
  accrualTotal: number;
  revenueTotal: number;
};

export type MetrologistGasSummaryBlock = {
  verification: { quantity: number };
  accrualTotal: number;
  revenueTotal: number;
};

export const METROLOGIST_WATER_ROW_KEYS = [
  'verification',
  'diagnosticsWater',
  'waterCounters',
  'replacement',
  'sealing',
] as const;
export const METROLOGIST_HEAT_ROW_KEYS = ['verification', 'mounting', 'sale', 'batteryReplacement'] as const;
export const METROLOGIST_GAS_ROW_KEYS = ['verification'] as const;

export const DEFAULT_METROLOGIST_ROW_LABELS: {
  water: Record<(typeof METROLOGIST_WATER_ROW_KEYS)[number], string>;
  heat: Record<(typeof METROLOGIST_HEAT_ROW_KEYS)[number], string>;
  gas: Record<(typeof METROLOGIST_GAS_ROW_KEYS)[number], string>;
} = {
  water: {
    verification: 'Поверка счетчиков',
    diagnosticsWater: 'Диагностика счетчика воды',
    waterCounters: 'Счетчики воды',
    replacement: 'Замена счетчиков',
    sealing: 'Опломбировка',
  },
  heat: {
    verification: 'Поверка счетчиков',
    mounting: 'Монтаж / демонтаж',
    sale: 'Продажа',
    batteryReplacement: 'Замена АКБ',
  },
  gas: {
    verification: 'Поверка счетчиков (газ)',
  },
};

export type MetrologistWork = {
  employee: {
    id: string;
    fullName: string;
    city?: string;
  };
  period: { from: string; to: string };
  totalReward: number;
  summary: {
    water: {
      nonBank: MetrologistWaterSummaryBlock;
      bankAccount: MetrologistWaterSummaryBlock;
    };
    heat: {
      nonBank: MetrologistHeatSummaryBlock;
      bankAccount: MetrologistHeatSummaryBlock;
    };
    gas: {
      nonBank: MetrologistGasSummaryBlock;
      bankAccount: MetrologistGasSummaryBlock;
    };
  };
  pagination?: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  work: Array<{
    dealId: string;
    title: string;
    closedAt: string;
    dealUrl?: string;
    paymentByBankAccount?: boolean;
    products: Array<{
      variantId: string;
      serviceName?: string;
      variantName?: string;
      name: string;
      quantity: number;
      price: number;
      reward: number;
    }>;
  }>;
};

export type ReportEntryEntityType =
  | 'bonusExtra'
  | 'gsm'
  | 'revenueHandover'
  | 'expense'
  | 'plan'
  | 'serviceCost'
  | 'householdStock'
  | 'businessTrip';

export type ReportEntryAuditAction = 'CREATE' | 'UPDATE' | 'DELETE';

export interface ReportEntryAuditLog {
  id: string;
  entityType: ReportEntryEntityType;
  action: ReportEntryAuditAction;
  entryId?: string | null;
  city?: string | null;
  actorUserId: string;
  actorLogin: string;
  actorType: string;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  createdAt: string;
}

export const REPORT_ENTRY_ENTITY_LABELS: Record<ReportEntryEntityType, string> = {
  bonusExtra: 'Доп начисления',
  gsm: 'ГСМ',
  revenueHandover: 'Сдача выручки',
  expense: 'Расходы',
  plan: 'План',
  serviceCost: 'Себестоимость',
  householdStock: 'Хоз блок',
  businessTrip: 'Командировки',
};

export const REPORT_ENTRY_ACTION_LABELS: Record<ReportEntryAuditAction, string> = {
  CREATE: 'Создание',
  UPDATE: 'Изменение',
  DELETE: 'Удаление',
};

const isGatewayTimeoutError = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 502 || status === 503 || status === 504;
};

async function withGatewayRetry<T>(fn: () => Promise<T>, retries = 2, delayMs = 2500): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (!isGatewayTimeoutError(error) || attempt === retries) {
        throw error;
      }
      await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
    }
  }
  throw lastError;
}

export const reportApi = {
  getSalaryServices: async () => {
    const response = await api.get('/api/v1/report/services');
    return response.data as SalaryService[];
  },

  createSalaryService: async (data: SalaryServicePayload) => {
    const response = await api.post('/api/v1/report/services', data);
    return response.data as SalaryService;
  },

  updateSalaryService: async (id: string, data: SalaryServicePayload) => {
    const response = await api.put(`/api/v1/report/services/${id}`, data);
    return response.data as SalaryService;
  },

  archiveSalaryService: async (id: string) => {
    const response = await api.delete(`/api/v1/report/services/${id}`);
    return response.data;
  },

  getEmployees: async (params?: { role?: EmployeeRole; city?: string; isActive?: boolean }) => {
    const response = await api.get('/api/v1/report/employees', { params });
    return response.data as Employee[];
  },

  createEmployee: async (data: EmployeePayload) => {
    const response = await api.post('/api/v1/report/employees', data);
    return response.data as Employee;
  },

  updateEmployee: async (id: string, data: Partial<EmployeePayload>) => {
    const response = await api.put(`/api/v1/report/employees/${id}`, data);
    return response.data as Employee;
  },

  archiveEmployee: async (id: string) => {
    const response = await api.delete(`/api/v1/report/employees/${id}`);
    return response.data;
  },

  listBonusExtras: async (params?: { city?: string; employeeId?: string; from?: string; to?: string }) => {
    const response = await api.get('/api/v1/report/bonus-extras', { params });
    return response.data as BonusExtraEntry[];
  },

  createBonusExtra: async (data: {
    city: string;
    employeeId: string;
    amount: number;
    date: string;
    comment?: string;
  }) => {
    const response = await api.post('/api/v1/report/bonus-extras', data);
    return response.data as BonusExtraEntry;
  },

  updateBonusExtra: async (
    id: string,
    data: { city: string; employeeId: string; amount: number; date: string; comment?: string },
  ) => {
    const response = await api.put(`/api/v1/report/bonus-extras/${id}`, data);
    return response.data as BonusExtraEntry;
  },

  deleteBonusExtra: async (id: string) => {
    const response = await api.delete(`/api/v1/report/bonus-extras/${id}`);
    return response.data as { ok: boolean };
  },

  listGsmEntries: async (params?: { city?: string; employeeId?: string; from?: string; to?: string }) => {
    const response = await api.get('/api/v1/report/gsm', { params });
    return response.data as GsmEntry[];
  },

  createGsmEntry: async (data: { city: string; employeeId: string; amount: number; date: string; comment?: string }) => {
    const response = await api.post('/api/v1/report/gsm', data);
    return response.data as GsmEntry;
  },

  updateGsmEntry: async (
    id: string,
    data: { city: string; employeeId: string; amount: number; date: string; comment?: string },
  ) => {
    const response = await api.put(`/api/v1/report/gsm/${id}`, data);
    return response.data as GsmEntry;
  },

  deleteGsmEntry: async (id: string) => {
    const response = await api.delete(`/api/v1/report/gsm/${id}`);
    return response.data as { ok: boolean };
  },

  listRevenueHandovers: async (params?: {
    city?: string;
    employeeId?: string;
    from?: string;
    to?: string;
  }) => {
    const response = await api.get('/api/v1/report/revenue-handovers', { params });
    return response.data as RevenueHandoverEntry[];
  },

  createRevenueHandover: async (data: {
    city: string;
    employeeId?: string;
    amount: number;
    date: string;
    comment?: string;
  }) => {
    const response = await api.post('/api/v1/report/revenue-handovers', data);
    return response.data as RevenueHandoverEntry;
  },

  updateRevenueHandover: async (
    id: string,
    data: { city: string; employeeId?: string; amount: number; date: string; comment?: string },
  ) => {
    const response = await api.put(`/api/v1/report/revenue-handovers/${id}`, data);
    return response.data as RevenueHandoverEntry;
  },

  deleteRevenueHandover: async (id: string) => {
    const response = await api.delete(`/api/v1/report/revenue-handovers/${id}`);
    return response.data as { ok: boolean };
  },

  listExpenses: async (params?: { city?: string; employeeId?: string; from?: string; to?: string }) => {
    const response = await api.get('/api/v1/report/expenses', { params });
    return response.data as ExpenseEntry[];
  },

  createExpense: async (data: {
    city: string;
    employeeId?: string;
    paymentType?: 'cash' | 'bank';
    amount: number;
    date: string;
    comment?: string;
  }) => {
    const response = await api.post('/api/v1/report/expenses', data);
    return response.data as ExpenseEntry;
  },

  updateExpense: async (
    id: string,
    data: {
      city: string;
      employeeId?: string;
      paymentType?: 'cash' | 'bank';
      amount: number;
      date: string;
      comment?: string;
    },
  ) => {
    const response = await api.put(`/api/v1/report/expenses/${id}`, data);
    return response.data as ExpenseEntry;
  },

  deleteExpense: async (id: string) => {
    const response = await api.delete(`/api/v1/report/expenses/${id}`);
    return response.data as { ok: boolean };
  },

  listPlans: async (params?: { city?: string; year?: number; month?: number }) => {
    const response = await api.get('/api/v1/report/plans', { params });
    return response.data as PlanEntry[];
  },

  upsertPlan: async (data: { city: string; year: number; month: number; amount: number; comment?: string }) => {
    const response = await api.post('/api/v1/report/plans', data);
    return response.data as PlanEntry;
  },

  updatePlan: async (
    id: string,
    data: { city: string; year: number; month: number; amount: number; comment?: string },
  ) => {
    const response = await api.put(`/api/v1/report/plans/${id}`, data);
    return response.data as PlanEntry;
  },

  deletePlan: async (id: string) => {
    const response = await api.delete(`/api/v1/report/plans/${id}`);
    return response.data as { ok: boolean };
  },

  listServiceCosts: async (params?: { city?: string; year?: number; month?: number }) => {
    const response = await api.get('/api/v1/report/service-costs', { params });
    return response.data as ServiceCostEntry[];
  },

  upsertServiceCost: async (
    data: {
      city: string;
      year: number;
      month: number;
    } & Partial<
      Omit<ServiceCostEntry, 'id' | 'city' | 'year' | 'month' | 'createdAt' | 'updatedAt'>
    >,
  ) => {
    const response = await api.post('/api/v1/report/service-costs', data);
    return response.data as ServiceCostEntry;
  },

  updateServiceCost: async (
    id: string,
    data: {
      city: string;
      year: number;
      month: number;
    } & Partial<
      Omit<ServiceCostEntry, 'id' | 'city' | 'year' | 'month' | 'createdAt' | 'updatedAt'>
    >,
  ) => {
    const response = await api.put(`/api/v1/report/service-costs/${id}`, data);
    return response.data as ServiceCostEntry;
  },

  deleteServiceCost: async (id: string) => {
    const response = await api.delete(`/api/v1/report/service-costs/${id}`);
    return response.data as { ok: boolean };
  },

  listBusinessTrips: async (params?: { city?: string; employeeId?: string; from?: string; to?: string }) => {
    const response = await api.get('/api/v1/report/business-trips', { params });
    return response.data as BusinessTrip[];
  },

  createBusinessTrip: async (data: {
    city: string;
    employeeId: string;
    from: string;
    to: string;
    comment?: string;
  }) => {
    const response = await api.post('/api/v1/report/business-trips', data);
    return response.data as BusinessTrip;
  },

  updateBusinessTrip: async (
    id: string,
    data: {
      city: string;
      employeeId: string;
      from: string;
      to: string;
      comment?: string;
    },
  ) => {
    const response = await api.put(`/api/v1/report/business-trips/${id}`, data);
    return response.data as BusinessTrip;
  },

  deleteBusinessTrip: async (id: string) => {
    const response = await api.delete(`/api/v1/report/business-trips/${id}`);
    return response.data as { ok: boolean };
  },

  listHouseholdStockReceipts: async (params?: { city?: string; from?: string; to?: string }) => {
    const response = await api.get('/api/v1/report/household-stock/receipts', { params });
    return response.data as HouseholdStockReceipt[];
  },

  createHouseholdStockReceipt: async (data: {
    city: string;
    itemType: HouseholdItemType;
    quantity: number;
    date: string;
    comment?: string;
  }) => {
    const response = await api.post('/api/v1/report/household-stock/receipts', data);
    return response.data as HouseholdStockReceipt;
  },

  updateHouseholdStockReceipt: async (
    id: string,
    data: {
      city: string;
      itemType: HouseholdItemType;
      quantity: number;
      date: string;
      comment?: string;
    },
  ) => {
    const response = await api.put(`/api/v1/report/household-stock/receipts/${id}`, data);
    return response.data as HouseholdStockReceipt;
  },

  deleteHouseholdStockReceipt: async (id: string) => {
    const response = await api.delete(`/api/v1/report/household-stock/receipts/${id}`);
    return response.data as { ok: boolean };
  },

  getHouseholdStockSummary: async (params: {
    city: string;
    from: string;
    to: string;
    employeeId?: string;
  }) => {
    const response = await api.get('/api/v1/report/household-stock/summary', {
      params,
      timeout: 300000,
    });
    return response.data as HouseholdStockSummary;
  },

  listHouseholdStockMovements: async (params?: {
    city?: string;
    employeeId?: string;
    itemType?: HouseholdItemType;
    scope?: HouseholdMovementScope;
    direction?: HouseholdMovementDirection;
    operationType?: HouseholdMovementOperation;
    from?: string;
    to?: string;
  }) => {
    const response = await api.get('/api/v1/report/household-stock/movements', { params });
    return response.data as HouseholdStockMovement[];
  },

  createHouseholdStockMovement: async (data: {
    city: string;
    itemType: HouseholdItemType;
    quantity: number;
    scope: HouseholdMovementScope;
    direction: HouseholdMovementDirection;
    operationType: HouseholdMovementOperation;
    employeeId?: string;
    dealId?: string;
    date: string;
    comment?: string;
  }) => {
    const response = await api.post('/api/v1/report/household-stock/movements', data);
    return response.data as HouseholdStockMovement | { warehouseMove: HouseholdStockMovement; employeeMove: HouseholdStockMovement };
  },

  updateHouseholdStockMovement: async (
    id: string,
    data: {
      city: string;
      itemType: HouseholdItemType;
      quantity: number;
      scope: HouseholdMovementScope;
      direction: HouseholdMovementDirection;
      operationType: HouseholdMovementOperation;
      employeeId?: string;
      dealId?: string;
      date: string;
      comment?: string;
    },
  ) => {
    const response = await api.put(`/api/v1/report/household-stock/movements/${id}`, data);
    return response.data as HouseholdStockMovement;
  },

  deleteHouseholdStockMovement: async (id: string, params?: { forceAdjust?: boolean }) => {
    const response = await api.delete(`/api/v1/report/household-stock/movements/${id}`, { params });
    return response.data as { ok: boolean };
  },

  getHouseholdStockBalances: async (params: { city: string; employeeId?: string; from?: string; to?: string }) => {
    const response = await api.get('/api/v1/report/household-stock/balances', { params });
    return response.data as HouseholdStockBalances;
  },

  recalculateHouseholdStockConsume: async (data: {
    city: string;
    from: string;
    to: string;
    employeeId?: string;
    forceAdjust?: boolean;
  }) => {
    const response = await api.post('/api/v1/report/household-stock/recalculate-consume', data);
    return response.data as {
      scannedDeals: number;
      created: number;
      skippedExisting: number;
      skippedNoDealId: number;
      adjustmentsCreated: number;
      errors: Array<{ dealId?: string; employeeId: string; itemType: HouseholdItemType; error: string }>;
    };
  },

  fetchMetrologistWork: async (
    employeeId: string,
    from: string,
    to: string,
    page: number = 1,
    pageSize: number = 50,
    workCity?: string,
  ) => {
    const response = await api.get('/api/v1/report/metrologist-work', {
      params: { employeeId, from, to, workCity, page, pageSize },
    });
    return response.data as MetrologistWork;
  },

  fetchMetrologistCityWork: async (
    exportCity: string,
    from: string,
    to: string,
    page: number = 1,
    pageSize: number = 50,
  ) => {
    return withGatewayRetry(async () => {
      const response = await api.get('/api/v1/report/metrologist-work/city', {
        params: { exportCity, from, to, page, pageSize },
        timeout: 300000,
      });
      return response.data as MetrologistWork & {
        cityAggregate?: {
          employeesTotal: number;
          employeesSucceeded: number;
          employeesFailed: number;
          allDeals: MetrologistWork['work'];
          cityCashSummary?: {
            cashInRegister: number;
            surrenderedRevenueAllTime: number;
            leftToHandoverPeriod: number;
          };
          employeeRows?: Array<{
            employeeId: string;
            fullName: string;
            salaryAccrued: number;
            unsurrenderedRevenue: number;
            cashRevenue: number;
            bankRevenue: number;
            totalRevenue: number;
            gsm: number;
          }>;
        };
      };
    });
  },

  downloadMetrologistWorkExcel: async (params: {
    from: string;
    to: string;
    employeeId?: string;
    employeeIds?: string[];
    exportCity?: string;
  }) => {
    const requestParams: Record<string, string> = {
      from: params.from,
      to: params.to,
    };
    if (params.exportCity?.trim()) {
      requestParams.exportCity = params.exportCity.trim();
    } else if (params.employeeIds && params.employeeIds.length > 0) {
      requestParams.employeeIds = params.employeeIds.join(',');
    } else if (params.employeeId) {
      requestParams.employeeId = params.employeeId;
    }
    return withGatewayRetry(async () => {
      const response = await api.get('/api/v1/report/metrologist-work/excel', {
        params: requestParams,
        responseType: 'blob',
        timeout: 300000,
      });
      return response;
    });
  },

  downloadMetrologistWorkPdf: async (params: {
    from: string;
    to: string;
    employeeId?: string;
    employeeIds?: string[];
    exportCity?: string;
  }) => {
    const requestParams: Record<string, string> = {
      from: params.from,
      to: params.to,
    };
    if (params.exportCity?.trim()) {
      requestParams.exportCity = params.exportCity.trim();
    } else if (params.employeeIds && params.employeeIds.length > 0) {
      requestParams.employeeIds = params.employeeIds.join(',');
    } else if (params.employeeId) {
      requestParams.employeeId = params.employeeId;
    }
    const response = await api.get('/api/v1/report/metrologist-work/pdf', {
      params: requestParams,
      responseType: 'blob',
    });
    return response;
  },

  listEntryAuditLog: async (params?: {
    entityType?: ReportEntryEntityType;
    city?: string;
    actorLogin?: string;
    actorType?: 'admin' | 'salary';
    from?: string;
    to?: string;
  }) => {
    const response = await api.get('/api/v1/report/entry-audit-log', { params });
    return response.data as ReportEntryAuditLog[];
  },
};
